import { formatAud } from "../income-tests/index.js";
import type { MeansTestTrace, PackagingShockOutput } from "./types.js";

// Plain-English copy for packaging-shock. Information, not advice (validation
// §3): we state the mechanism — "this benefit is counted as income by N of these
// tests, here is the figure and the source" — with NO "save $X / you should
// lease / packaging is a trap" framing. The both-sides rule is structural: the
// summary ALWAYS pairs the income-tax saving with the income-test flow-on. Pure
// string-builders.

const TEST_LABELS: Record<MeansTestTrace["test"], string> = {
  mls: "Medicare levy surcharge",
  div293: "Division 293",
  help: "HELP/STSL repayment income",
  ccs: "Child Care Subsidy / Family Tax Benefit (adjusted taxable income)",
};

/**
 * The headline both-sides line: the income-tax saving the packaging delivers AND
 * the income tests the same benefit moves. Never one without the other — this is
 * the panel's binding rule, rendered as a single sentence so a surface cannot
 * show the downside alone.
 */
export function bothSidesSummary(out: PackagingShockOutput): string {
  const moved = out.means.filter((m) => m.crossesThreshold).length;
  const saving = formatAud(out.incomeTaxSaving);
  if (moved === 0) {
    return (
      `Packaging this benefit reduces your income tax and Medicare levy by about ` +
      `${saving} this year. On the figures you entered it does not move you across ` +
      `any of the four income-test thresholds we checked. Both effects are shown so ` +
      `you can weigh the saving against the income-test position.`
    );
  }
  return (
    `Packaging this benefit reduces your income tax and Medicare levy by about ` +
    `${saving} this year. The same benefit is also counted as income by ${moved} of ` +
    `the four income tests we checked, changing your position in each. We show both ` +
    `the tax saving and the income-test flow-on so you can see the full picture, ` +
    `not just one side.`
  );
}

/** One information-only line per traced test. States the mechanism, not a verdict. */
export function traceBullet(trace: MeansTestTrace): string {
  const label = TEST_LABELS[trace.test];
  const delta = formatAud(trace.incomeDelta);
  if (trace.incomeDelta <= 0) {
    return `${label}: this benefit does not increase the income this test uses.`;
  }
  const crossing = trace.crossesThreshold
    ? " This is enough to move you across the test's threshold on the figures you entered."
    : " This stays within your current band on the figures you entered.";
  return (
    `${label}: the reported benefit adds about ${delta} to the income this test ` +
    `uses.${crossing}`
  );
}

/**
 * Explains WHY the same benefit can count as a different number in different
 * tests — the load-bearing exempt-vs-non-exempt mechanic. States it without
 * overstating any CCS loss.
 */
export function asymmetryBullet(out: PackagingShockOutput): string {
  const grossed = out.with.grossedUpFringeBenefits;
  if (grossed === 0) {
    return "Your benefit's taxable value is at or below the $2,000 reporting trigger, so no reportable fringe benefit flows into the income tests.";
  }
  const adjusted = out.with.adjustedFringeBenefitsTotal;
  if (adjusted === grossed) {
    return (
      `Your benefit is reported as a fringe benefit of ${formatAud(grossed)} and ` +
      `counts at that full value in every income test, including the family-` +
      `assistance adjusted taxable income — because it is not exempt-employer ` +
      `(section 57A) packaging.`
    );
  }
  return (
    `Your benefit is reported as a fringe benefit of ${formatAud(grossed)}. The ` +
    `Medicare levy surcharge, Division 293 and HELP/STSL repayment income count it ` +
    `at that full value, while the family-assistance adjusted taxable income (Child ` +
    `Care Subsidy and Family Tax Benefit) counts the exempt-employer portion at a ` +
    `reduced value — ${formatAud(adjusted)}. That is why the same benefit appears as ` +
    `a different number in different tests.`
  );
}

/** Source-attribution + mandatory disclaimer line. */
export function sourceBullet(ruleVersion: string, verifiedAt: string): string {
  return (
    `These figures use the ATO and Services Australia income-test definitions in ` +
    `force for ${ruleVersion} (last verified ${verifiedAt}). Educational tool. Not ` +
    `financial advice. The ATO and Services Australia finalise your actual figures ` +
    `— always check ato.gov.au and servicesaustralia.gov.au.`
  );
}

export function explanationBullets(
  out: PackagingShockOutput,
  ruleVersion: string,
  verifiedAt: string,
): string[] {
  return [
    bothSidesSummary(out),
    asymmetryBullet(out),
    ...out.means.map(traceBullet),
    sourceBullet(ruleVersion, verifiedAt),
  ];
}
